import { observer } from 'mobx-react-lite';
import { chooseEndDateLabel, removeEndDate } from './RecurrenceSummary.locstring.json';
import loc from 'owa-localize';
import { IButtonStyles, CommandButton, IButton } from '@fluentui/react/lib/Button';
import { Callout } from '@fluentui/react/lib/Callout';
import { Icon } from '@fluentui/react/lib/Icon';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { getOptimalTextColorString } from 'owa-color-utils/lib/colorUtils';
import { ControlIcons } from 'owa-control-icons';
import { InlineDatePicker } from 'owa-datepicker';
import { OwaDate, owaDate } from 'owa-datetime';
import getRecurrenceStrings from 'owa-recurrence-utils/lib/getRecurrenceStrings';
import { updateRecurrenceEndUtil } from 'owa-recurrence-utils/lib/recurrenceSummaryUtils';
import updateRecurrenceEnd from 'owa-recurrence-utils/lib/updateRecurrenceEnd';
import RecurrenceType from 'owa-service/lib/contract/RecurrenceType';

import { getPaletteAsRawColors } from 'owa-theme';
import { CalendarEvent } from 'owa-calendar-types';

import * as React from 'react';

let styles = require('./RecurrenceSummary.scss');
let classNames = require('classnames/bind').bind(styles);

export interface RecurrenceSummaryProps {
    timeZoneId: string;
    event: CalendarEvent;
    recurrence: RecurrenceType;
    onEndRecurrenceChangedCallback: (recurrence: RecurrenceType) => void;
    colorScheme: ColorScheme;
    cssVariablesStyle?: any;
}

export interface RecurrenceSummaryState {
    isEndDateSelectorVisible: boolean;
}

export default observer(function RecurrenceSummary(props: RecurrenceSummaryProps) {
    const recurrenceEndElement = React.useRef<HTMLElement>();
    const datePickerButton = React.useRef<IButton>();
    const [isEndDateSelectorVisible, setIsEndDateSelectorVisible] = React.useState<boolean>(false);
    const bindRecurrenceEndElementRef = ref => (recurrenceEndElement.current = ref);
    const bindDatePickerButtonRef = ref => (datePickerButton.current = ref);
    const updateRecurrenceEndDate = (newEnd: OwaDate) => {
        updateRecurrenceEndUtil(newEnd, props.recurrence, props.onEndRecurrenceChangedCallback);
        onCalloutDismiss();
        // This is used to fix a callout dismissal bug in IE11 with React 16 and Fabric Contextual Menus
        datePickerButton.current.focus();
    };
    const toggleEndDateSelectorVisibility = () => {
        setIsEndDateSelectorVisible(!isEndDateSelectorVisible);
    };
    const onCalloutDismiss = () => {
        setIsEndDateSelectorVisible(false);
    };
    const onRemoveEndDate = () => {
        props.onEndRecurrenceChangedCallback(updateRecurrenceEnd(props.recurrence, null));
        datePickerButton.current.focus();
    };
    let { timeZoneId, event, recurrence, cssVariablesStyle } = props;
    let {
        recurrenceString,
        ariaLabel,
        recurrenceDisplayDate,
        recurrenceEnd,
    } = getRecurrenceStrings(timeZoneId, event, recurrence);
    const recurrenceStart = owaDate(timeZoneId, recurrence.RecurrenceRange.StartDate);
    let cssVariablesButtonStyle: IButtonStyles;
    let recurrenceCssVariablesStyle;
    // If cssVariablesStyle exist, it means that we populated the correct style based on cal-css-variables flag
    if (cssVariablesStyle) {
        cssVariablesButtonStyle = cssVariablesStyle.recurrenceSummaryButton;
        recurrenceCssVariablesStyle = cssVariablesStyle.cssVariabelsStyle;
    }
    const textColor = getOptimalTextColorString(
        getPaletteAsRawColors().white,
        props.colorScheme,
        props.colorScheme.calPrimary
    );
    const rootHovererColor = getOptimalTextColorString(
        getPaletteAsRawColors().white,
        props.colorScheme,
        props.colorScheme.calDarker
    );
    const rootPressedColor = getOptimalTextColorString(
        getPaletteAsRawColors().white,
        props.colorScheme,
        props.colorScheme.calPrimary
    );
    recurrenceCssVariablesStyle = !recurrenceCssVariablesStyle ? {} : recurrenceCssVariablesStyle;
    recurrenceCssVariablesStyle.root = { color: textColor };
    recurrenceCssVariablesStyle.rootHovered = { color: rootHovererColor };
    recurrenceCssVariablesStyle.rootPressed = { color: rootPressedColor };
    return (
        <div className={styles.summaryDiv}>
            <div className={styles.summary}>
                <span className={styles.summaryString}>{recurrenceString}</span>
                <div className={styles.datePickerWrapper} ref={bindRecurrenceEndElementRef}>
                    <CommandButton
                        componentRef={bindDatePickerButtonRef}
                        styles={recurrenceCssVariablesStyle}
                        className={classNames(styles.datePicker, cssVariablesButtonStyle)}
                        onClick={toggleEndDateSelectorVisibility}
                        ariaLabel={ariaLabel}>
                        {recurrenceEnd && recurrenceDisplayDate}
                        {!recurrenceEnd && loc(chooseEndDateLabel)}
                        {recurrenceEnd && (
                            <Icon className={styles.chevron} iconName={ControlIcons.ChevronDown} />
                        )}
                    </CommandButton>
                    {recurrenceEnd && (
                        <CommandButton
                            styles={recurrenceCssVariablesStyle}
                            className={classNames(styles.datePicker, cssVariablesButtonStyle)}
                            onClick={onRemoveEndDate}>
                            {loc(removeEndDate)}
                        </CommandButton>
                    )}
                </div>
            </div>
            {isEndDateSelectorVisible && (
                <Callout
                    isBeakVisible={false}
                    target={recurrenceEndElement.current}
                    onDismiss={onCalloutDismiss}
                    setInitialFocus={true}>
                    <InlineDatePicker
                        timeZoneId={timeZoneId}
                        value={recurrenceEnd}
                        minDate={recurrenceStart}
                        onSelectDate={updateRecurrenceEndDate}
                    />
                </Callout>
            )}
        </div>
    );
});
